import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import candidates from '../data/candidates.json';

const THEME = {
  primary: '#2563eb',
  accent: '#f97316',
  dark: '#0f172a',
};

const PROFESI = [
  { key: 'Asisten Rumah Tangga', tKey: 'kandidatBanner.profesi.art' },
  { key: 'Babysitter',           tKey: 'kandidatBanner.profesi.babysitter' },
  { key: 'Perawat Lansia',       tKey: 'kandidatBanner.profesi.elderCare' },
];

export default function CandidateBanner() {
  const { t } = useTranslation();
  const total = candidates.length;

  return (
    <Box
      sx={{
        bgcolor: THEME.dark,
        py: { xs: 5, md: 6 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle decorative rings */}
      <Box sx={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -100, left: -60, width: 320, height: 320, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
      {/* Orange accent line at top */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.accent})` }} />

      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 4, md: 3 }}
        >
          {/* Left: headline */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ade80', boxShadow: '0 0 8px #4ade80' }} />
              <Typography sx={{ color: '#4ade80', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 1.8 }}>
                {t('kandidatBanner.available')}
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 900,
                fontSize: { xs: '1.7rem', md: '2.1rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                mb: 1,
              }}
            >
              {t('kandidatBanner.headline', { count: total })}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.88rem', lineHeight: 1.6, maxWidth: 420 }}>
              {t('kandidatBanner.subtitle')}
            </Typography>
          </Box>

          {/* Right: profesi pills + CTA */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ flexShrink: 0 }}
          >
            {/* Per-profesi count pills */}
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {PROFESI.map(({ key, tKey }) => {
                const count = candidates.filter(c => c.profesi === key).length;
                if (count === 0) return null;
                return (
                  <Box
                    key={key}
                    component={Link}
                    to="/kandidat"
                    state={{ profesi: key }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.75,
                      py: 0.75,
                      borderRadius: 99,
                      border: '1px solid rgba(255,255,255,0.1)',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      textDecoration: 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', fontWeight: 600 }}>
                      {t(tKey)}
                    </Typography>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: THEME.accent, flexShrink: 0 }} />
                    <Typography sx={{ color: '#fff', fontSize: '0.78rem', fontWeight: 800 }}>
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            <Button
              component={Link}
              to="/kandidat"
              variant="contained"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor: THEME.accent,
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.88rem',
                textTransform: 'none',
                borderRadius: 99,
                px: 3,
                py: 1.25,
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 20px rgba(249,115,22,0.35)',
                '&:hover': { bgcolor: '#ea6c10', boxShadow: '0 6px 24px rgba(249,115,22,0.45)', transform: 'translateY(-1px)' },
                transition: 'all 0.2s ease',
              }}
            >
              {t('kandidatBanner.cta')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
